import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ROLE_LABELS } from '@shared/roles';
import type { OrgRole } from '@shared/types';
import { ApiError, api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, CardHeader, Skeleton } from '../components/ui';
import { Field, Input, Notice, PasswordInput } from '../components/form';

interface Preview {
  tenantName: string;
  email: string;
  emailMasked: string;
  role: OrgRole;
  expiresAt: string;
  needsProfile: boolean;
}

/**
 * 接受邀请。
 *
 * 已有账号的人接受邀请时**不需要再设口令**（否则等于用一次邀请把别人的口令改掉）；
 * 全新邮箱才要求填姓名与新口令。
 */
export function InvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const preview = useAsync<Preview>(() => api.get(`/api/invitations/preview?token=${encodeURIComponent(token)}`), [token]);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const result = await api.post<{ email: string }>('/api/invitations/accept', {
        token,
        name: name.trim(),
        password,
      });
      setDone(result.email);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFields(caught.fieldErrors);
        setError(Object.keys(caught.fieldErrors).length === 0 ? caught.message : null);
      } else {
        setError(describeError(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader title="接受邀请" />

          {!token ? (
            <Notice tone="danger">邀请链接不完整，请向邀请你的人重新索取。</Notice>
          ) : preview.loading ? (
            <Skeleton className="h-40 w-full" />
          ) : preview.error ? (
            <Notice tone="danger">{preview.error.message}</Notice>
          ) : done ? (
            <div className="space-y-4">
              <Notice tone="ok">
                已加入组织「{preview.data?.tenantName}」。现在可以用 <strong>{done}</strong> 登录。
              </Notice>
              <Button variant="primary" block onClick={() => navigate('/login')}>
                前往登录
              </Button>
            </div>
          ) : (
            <>
              <p className="text-[15px] text-fg">
                <span className="font-semibold">{preview.data?.tenantName}</span>
                <span className="text-fg-muted"> 邀请你以「{ROLE_LABELS[preview.data?.role ?? 'member']}」身份加入</span>
              </p>
              <p className="mt-1 text-[13px] text-fg-muted">
                邀请发送至 <span className="font-mono">{preview.data?.emailMasked}</span>
              </p>

              <form
                className="mt-5 space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void accept();
                }}
              >
                {preview.data?.needsProfile ? (
                  <>
                    <Field label="你的姓名" htmlFor="invite-name" required error={fields.name}>
                      <Input id="invite-name" value={name} onChange={setName} autoFocus required />
                    </Field>
                    <Field
                      label="设置口令"
                      htmlFor="invite-password"
                      required
                      error={fields.password}
                      hint="至少 10 位，含字母与数字"
                    >
                      <PasswordInput
                        id="invite-password"
                        value={password}
                        onChange={setPassword}
                        autoComplete="new-password"
                        required
                      />
                    </Field>
                  </>
                ) : (
                  <Notice tone="info">
                    该邮箱已有 Havorix 账号，接受后直接加入本组织，口令不会被改动。
                  </Notice>
                )}

                {error ? (
                  <p className="text-[13px] text-danger" role="alert">
                    {error}
                  </p>
                ) : null}

                <Button type="submit" variant="primary" block loading={busy}>
                  接受邀请
                </Button>
              </form>

              <p className="mt-4 text-xs text-fg-muted">
                已经有账号了？
                <Link to="/login" className="ml-1 text-brand hover:underline">
                  直接登录
                </Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
